import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import ResetButton from '../../../components/common/ResetButton';
import InvoiceFilter from '../../../components/Billing/Invoice/InvoiceFilter';
import { usePharmacyInvoicesQuery, usePharmacyBilledInvoicesQuery } from './pharmacyHelper';
import { useClinic } from '../../../components/ClinicProvider';
import PharmacyHeader from './PharmacyHeader';
import { PharmacyTabValue } from './PharmacyTabs';
import PharmacyCheckedInTable from './PharmacyCheckedInTable';
import PharmacyBilledTable from './PharmacyBilledTable';
import PharmacyPagination from './PharmacyPagination';
import HeaderProfile from '../../../components/header_Profile/header_Profile.index';
import { usePatientContext } from '../../../components/SubPatinetProvider';
import { useNavigate } from 'react-router-dom';

// --- Types ---
interface PharmacyFilterValues {
  fromDate: string;
  toDate: string;
  status: string;
}

// --- Constants ---
const DEFAULT_LIMIT = 10;

// --- Main Pharmacy Page ---
const PatientPharmacy = () => {
  // --- State ---
  const [activeTab, setActiveTab] = useState<PharmacyTabValue>('checkedIn');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState<number>(DEFAULT_LIMIT);
  const [filterOpenLegacy, setFilterOpenLegacy] = useState(false);
  const [filterValues, setFilterValues] = useState<PharmacyFilterValues>({ fromDate: '', toDate: '', status: '' });
  const  navigate = useNavigate();
  // Filter handlers
  const handleOpenLegacyFilter = () => setFilterOpenLegacy(true);
  const handleCloseLegacyFilter = () => setFilterOpenLegacy(false);

  const handleApplyFilter = (values: { fromDate: string; toDate: string; billType?: string; status?: string }) => {
    if (
      values.fromDate !== filterValues.fromDate ||
      values.toDate !== filterValues.toDate ||
      values.status !== filterValues.status
    ) {
      setFilterValues({ fromDate: values.fromDate, toDate: values.toDate, status: values.status || '' });
      setCurrentPage(1);
    }
  };

  const { ClinicId } = useClinic();
  const { patientId } = usePatientContext();
  
  // --- Handlers ---
  const handleTabChange = useCallback((tab: PharmacyTabValue) => {
    setActiveTab(tab);
    setCurrentPage(1);
    // Reset status filter when switching tabs to avoid showing incorrect status options
    setFilterValues(prev => ({ ...prev, status: '' }));
  }, []);
  const handleSearchChange = useCallback((val: string) => setSearch(val), []);
  const handleNewInvoice = useCallback(() => {
    if (!patientId) return;
    navigate(`/admin/billing/pharmacy/new/${patientId}`);
  }, [navigate, patientId]);
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);

  // Reset handler
  const handleReset = useCallback(() => {
    setSearch('');
    setFilterValues({ fromDate: '', toDate: '', status: '' });
    setCurrentPage(1);
  }, []);

  // Status badge click handler
  const handleStatusClick = useCallback((status: 'ordered' | 'unordered' | 'dispensed' | null) => {
    setFilterValues(prev => ({ ...prev, status: status || '' }));
    setCurrentPage(1);
  }, []);

  
  // Query for "Checked In" tab - uses pharmacy-invoice API
  const checkedInQuery = usePharmacyInvoicesQuery({
    clinic_id: Number(ClinicId),
    search: search || undefined,
    date_from: filterValues.fromDate || undefined,
    date_to: filterValues.toDate || undefined,
    patient_id: patientId ,
    prescription_status: filterValues.status ? (filterValues.status as 'ordered' | 'unordered') : undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    enabled: !!ClinicId && activeTab === 'checkedIn',
  });

  // Query for "Billed" tab - uses pharmacy-invoice/billed-invoices API
  const billedQuery = usePharmacyBilledInvoicesQuery({
    from_date: filterValues.fromDate || undefined,
    to_date: filterValues.toDate || undefined,
    patient_id: patientId ,
    search: search || undefined,
    status: filterValues.status ? (filterValues.status as 'paid' | 'unpaid' | 'partially_paid') : undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
    enabled: activeTab === 'billed',
  });

  // Select the appropriate query based on active tab 
  const { isLoading, error } = activeTab === 'billed' ? billedQuery : checkedInQuery;

  // Extract nested data from each API response
  const checkedInApiData = checkedInQuery.data?.data;
  const billedApiData = billedQuery.data?.data;

  // Prescription status counts (Ordered/Unordered) - only for Checked In tab
  const prescriptionStatusSummary = useMemo(() => {
    if (activeTab !== 'checkedIn') return undefined;
    const counts = checkedInApiData?.counts;
    return {
      ordered: counts?.ordered ?? 0,
      unordered: counts?.unordered ?? 0,
    };
  }, [activeTab, checkedInApiData]);

  // Pagination from total_count based on active tab
  const totalPages = useMemo(() => {
    const apiData = activeTab === 'billed' ? billedApiData : checkedInApiData;
    const total = apiData?.meta?.total_count || 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [activeTab, billedApiData, checkedInApiData, pageSize]);

  // Total entries for pagination display
  const totalEntries = useMemo(() => {
    const apiData = activeTab === 'billed' ? billedApiData : checkedInApiData;
    return apiData?.meta?.total_count || 0;
  }, [activeTab, billedApiData, checkedInApiData]);

  // Table data mapping for PharmacyCheckedInTable
  const checkedInTableData = useMemo(
    () =>
      (checkedInApiData?.objects ?? []).map((inv) => ({
        patient_id: inv.patient_id,
        appointment_id: inv.appointment_id ?? null,
        date: inv.date || '-',
        patientName: inv.patient_name ?? '-',
        patientAge: inv.age ?? '',
        patientMrn: inv.medical_record_no ?? '',
        patientGender: inv.gender ?? '',
        provider: inv.provider || '-',
        prescriptionStatus: inv.prescription_status || 'unordered',
        invoice_id: inv.invoice_id || '',
        dispense_number: inv.dispense_number || '',
      })),
    [checkedInApiData]
  );

  // Table data mapping for PharmacyBilledTable
  const billedTableData = useMemo(
    () =>
      (billedApiData?.objects ?? []).map((inv) => ({
        id: inv.id ?? inv.patient_id,
        patient_id: inv.patient_id,
        date: inv.date || '-',
        patientName: inv.patient_name ?? '-',
        patientAge: inv.age ?? '',
        patientMrn: inv.patient_mrn ?? '',
        patientGender: inv.gender ?? '',
        billNo: inv.bill_no ?? '-',
        totalAmt: inv.total_amt != null ? Number(inv.total_amt) : 0,
        concessionAmt: inv.concession_amt != null ? Number(inv.concession_amt) : 0,
        netAmt: inv.net_amt != null ? Number(inv.net_amt) : 0,
        paidAmt: inv.paid_amt != null ? Number(inv.paid_amt) : 0,
        balanceAmt: inv.balance_amt != null ? Number(inv.balance_amt) : 0,
        status: inv.status ?? 'unpaid',
        billRemark: inv.bill_remark ?? '',
      })),
    [billedApiData]
  );

  // Error alert
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line no-console
      console.error('Pharmacy fetch error', error);
    }
  }, [error]);

  // --- Render --- 
  return (
    <>
      <HeaderProfile />
      <Box className="pharmacy-page">
        {/* Header: Tabs, Filter, Search, Add New Invoice */}
        <PharmacyHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          search={search}
          showHeader={false}
          onSearchChange={handleSearchChange}
          onOpenFilter={handleOpenLegacyFilter}
          onNewInvoice={handleNewInvoice} />

        {/* Filter Modal */}
        <InvoiceFilter
          open={filterOpenLegacy}
          onClose={handleCloseLegacyFilter}
          onApply={handleApplyFilter}
          billTypeOptions={[]}
          statusOptions={activeTab === 'checkedIn'
            ? [
              { value: '', label: 'Select Status' },
              { value: 'ordered', label: 'Ordered' },
              { value: 'dispensed', label: 'Dispensed' },
              { value: 'unordered', label: 'Unordered' },
            ]
            : [
              { value: '', label: 'Select Status' },
              { value: 'paid', label: 'Paid' },
              { value: 'unpaid', label: 'Unpaid' },
              { value: 'partially_paid', label: 'Partially Paid' },
            ]}
          showBillType={false}
          showStatus={true}
          initialValues={{ ...filterValues, billType: '' }} />

        {/* Table - Conditional rendering based on active tab */}
        {activeTab === 'billed' ? (
          <PharmacyBilledTable showPatientColumn={false} data={billedTableData} loading={isLoading} />
        ) : (
          <PharmacyCheckedInTable showPatientColumn={false} data={checkedInTableData} loading={isLoading} />
        )}

        {/* Pagination with Ordered/Unordered badges */}
        <PharmacyPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalEntries={totalEntries}
          pageSize={pageSize}
          statusSummary={prescriptionStatusSummary}
          onStatusClick={handleStatusClick}
          selectedStatus={filterValues.status}
          rightContent={<ResetButton onClick={handleReset} sx={{ ml: 1, height: '28px' }} />} />
      </Box></>
  );
};

export default PatientPharmacy;
